import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { PRODUCTS } from "@/data/content";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/products" replace />;

  const idx = PRODUCTS.findIndex((p) => p.slug === slug);
  const next = PRODUCTS[(idx + 1) % PRODUCTS.length];
  const isExternal = product.link.startsWith("http");

  return (
    <div data-testid={`product-detail-${slug}`} className="bg-white">
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 border-b border-slate-200 overflow-hidden bg-slate-50">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${product.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-600 hover:text-[#1D4ED8]"
            data-testid="back-to-products"
          >
            <ArrowLeft size={14} weight="bold" /> All products
          </Link>
          <div className="mt-8 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline mb-5">Theta Dynamics product</div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900">
                {product.name}
              </h1>
              <p className="mt-6 text-lg md:text-2xl text-slate-700 max-w-3xl font-light">
                {product.tagline}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              {isExternal ? (
                <a
                  href={product.link}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid={`product-${slug}-cta`}
                >
                  {product.cta} <ArrowUpRight size={16} weight="bold" />
                </a>
              ) : (
                <Link to={product.link} className="btn-primary" data-testid={`product-${slug}-cta`}>
                  {product.cta} <ArrowUpRight size={16} weight="bold" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="overline mb-5">Overview</div>
            <p className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight leading-snug">
              {product.summary}
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-3 gap-3">
            {product.metrics.map((m) => (
              <div key={m.l} className="card-flat p-5">
                <div className="font-display text-2xl md:text-3xl font-black text-slate-900 tracking-tighter">
                  {m.v}
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {m.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-5">Capabilities</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight max-w-3xl">
            What you get with {product.name}.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {product.features.map((f, i) => (
              <div
                key={f}
                data-testid={`feature-${slug}-${i}`}
                className="card-flat p-7 flex gap-4 items-start"
              >
                <CheckCircle size={22} weight="bold" className="text-[#1D4ED8] shrink-0 mt-0.5" />
                <span className="text-sm md:text-base text-slate-800 leading-relaxed">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-6 items-stretch">
          <Link
            to="/contact"
            data-testid={`product-${slug}-contact-cta`}
            className="md:col-span-8 bg-[#1D4ED8] hover:bg-[#1E3A8A] transition-colors p-10 md:p-14 flex flex-col justify-between text-white"
          >
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/85 font-bold">Talk to the team</div>
            <h3 className="mt-6 font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1.05]">
              Bring {product.name} to your organisation.
            </h3>
            <div className="mt-10 inline-flex items-center gap-2 font-mono text-xs tracking-[0.22em] uppercase">
              Request a demo <ArrowUpRight size={16} weight="bold" />
            </div>
          </Link>

          <Link
            to={`/products/${next.slug}`}
            data-testid={`product-${slug}-next`}
            className="md:col-span-4 bg-white border border-slate-300 hover:border-[#1D4ED8] hover:bg-slate-50 transition-colors p-10 md:p-14 flex flex-col justify-between"
          >
            <div className="overline">Up next</div>
            <div>
              <div className="mt-6 font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
                {next.name}
              </div>
              <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs tracking-[0.22em] uppercase text-slate-600">
                Explore product <ArrowUpRight size={14} weight="bold" />
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
